import React from 'react';
import type {
  ChipProps } from '@mui/material';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Button,
  Alert,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useDeleteMagicLinkMutation } from 'api/services/dataEndpoints';
import type { IMagicLinkType } from 'types/apiTypes/userApiTypes';

dayjs.extend(relativeTime);

interface IAuthLinksListProps {
  links: IMagicLinkType[];
  email: string;
  refetchList: () => void;
  isLoading?: boolean;
}

const AuthLinksList: React.FC<IAuthLinksListProps> = ({ links, email, isLoading, refetchList }) => {
  const [deleteMagicLink] = useDeleteMagicLinkMutation();

  const handleDeleteLink = async (linkId: number) => {
    try {
      await deleteMagicLink(linkId).unwrap();
      refetchList();
    } catch (error) {
      console.error('Failed to delete link:', error);
    }
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const getLinkStatus = (link: IMagicLinkType) => {
    const now = dayjs();
    const expiresAt = dayjs(link.expires_at);

    if (link.is_used) {
      return { status: 'used', color: 'default', label: 'Used' };
    }
    if (expiresAt.isBefore(now)) {
      return { status: 'expired', color: 'error', label: 'Expired' };
    }
    if (expiresAt.diff(now, 'hour') < 24) {
      return { status: 'expiring', color: 'warning', label: 'Expiring soon' };
    }
    return { status: 'active', color: 'success', label: 'Active' };
  };

  const activeLinks = links.filter(
    (link) => !link.is_used && dayjs(link.expires_at).isAfter(dayjs()),
  );
  const expiredOrUsedLinks = links.filter(
    (link) => link.is_used || dayjs(link.expires_at).isBefore(dayjs()),
  );

  if (isLoading) {
    return <Typography>Loading links...</Typography>;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h3">
          Active Auth Links ({activeLinks.length})
        </Typography>
      </Box>

      {activeLinks.length === 0 ? (
        <Alert severity="info">
          No active links. Generate a new one above.
        </Alert>
      ) : (
        <Paper elevation={2}>
          <List>
            {activeLinks.map((link, index) => {
              const status = getLinkStatus(link);
              return (
                <React.Fragment key={link.id}>
                  <ListItem>
                    <LinkIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText
                      primary={
                        (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {link.link}
                          </Typography>
                          <Chip
                            label={status.label}
                            color={status.color as ChipProps['color']}
                            size="small"
                          />
                         </Box>)
                      }
                      secondary={
                        (<Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Created: {dayjs(link.created_at).format('MMM D, YYYY HH:mm')}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Expires: {dayjs(link.expires_at).format('MMM D, YYYY HH:mm')}
                            {' '}({dayjs(link.expires_at).fromNow()})
                          </Typography>
                         </Box>)
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="copy"
                        onClick={() => handleCopyLink(link.link)}
                        sx={{ mr: 1 }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteLink(link.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < activeLinks.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}

    </Box>
  );
};

export default AuthLinksList;
